import * as React from 'react';

import {
    faBars,
    faCar,
    faCircleParking,
    faHouse,
    faUser,
    faUsers,
    faWavePulse,
    faHeartPulse,
    faChartPieSimple,
    faTicketPerforated,
    faPiggyBank,
    faSliders,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';

import logo from 'src/assets/images/logo_white.png';
import { PERMISSION } from 'src/features/auth';
import NavigationSidebarButton from 'src/features/navigation/components/NavigationSidebarButton';
import { pageTitles } from 'src/features/navigation/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import useScreenSize from 'src/hooks/useScreenSize.ts';
import { useParking } from 'src/providers/ParkingProvider';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { Separator } from 'src/registry/ui/Separator';
import { cn } from 'src/utils';

import type { NavigationLinkDetail } from 'src/features/navigation/utils/types.ts';

const NavigationSidebar = () => {
    const { formatMessage } = useIntl();
    const { screenWidth } = useScreenSize();
    const permissionHome = useHasPermissions([PERMISSION.HOME]);
    const permissionDevices = useHasPermissions([PERMISSION.PARKING_DEVICES]);
    const permissionVehicle = useHasPermissions([PERMISSION.VEHICLE]);
    const permissionValidation = useHasPermissions([PERMISSION.VEHICLE_VALIDATION]);
    const permissionStats = useHasPermissions([PERMISSION.STATS]);
    const permissionUsers = useHasPermissions([PERMISSION.USERS]);
    const permissionMonitoring = useHasPermissions([PERMISSION.MONITORING]);
    const permissionParking = useHasPermissions(
        [
            PERMISSION.PARKING_DETAILS_MANAGE,
            PERMISSION.GUEST_REASONS_MANAGE,
            PERMISSION.PRICING,
            PERMISSION.RESERVATIONS_SETTINGS,
            PERMISSION.PARKING_ADMIN_SETTINGS,
            PERMISSION.SUBSCRIPTIONS_SETTINGS,
            PERMISSION.PARKING_NOTIFICATIONS_SETTINGS,
            PERMISSION.ADDITIONAL_SERVICES_MANAGE,
            PERMISSION.USERS_MANAGE,
        ],
        true,
    );
    const permissionProfile = useHasPermissions([PERMISSION.PROFILE]);
    const permissionCashCollections = useHasPermissions([PERMISSION.CASH_COLLECTIONS]);
    const permissionParkingAdminSettings = useHasPermissions([PERMISSION.PARKING_ADMIN_SETTINGS]);

    const [visibleNavLinksInMobile, setVisibleNavLinksInMobile] = React.useState<NavigationLinkDetail[]>([]);
    const { parkingId } = useParking();

    const navigationLinkDetails: NavigationLinkDetail[] = React.useMemo(() => {
        return [
            { link: '', icon: faHouse, tooltip: pageTitles.home, hasPermission: permissionHome },
            {
                link: 'status',
                icon: faWavePulse,
                tooltip: pageTitles.parkingStatus,
                hasPermission: permissionDevices,
            },
            {
                link: 'vehicles',
                icon: faCar,
                tooltip: pageTitles.vehicles,
                hasPermission: permissionVehicle,
            },
            {
                link: 'statistics/reports',
                icon: faChartPieSimple,
                tooltip: pageTitles.statistics,
                hasPermission: permissionStats,
            },
            {
                link: 'validation',
                icon: faTicketPerforated,
                tooltip: pageTitles.validation,
                hasPermission: permissionValidation,
            },
            {
                link: 'user-management',
                icon: faUsers,
                tooltip: pageTitles.users,
                hasPermission: permissionUsers,
            },
            {
                link: 'pay-stations',
                icon: faHeartPulse,
                tooltip: pageTitles.payStations,
                hasPermission: permissionMonitoring,
            },
            {
                link: 'cash-collections',
                icon: faPiggyBank,
                tooltip: pageTitles.cashCollections,
                hasPermission: permissionCashCollections,
            },
            {
                link: 'parking-admin',
                icon: faSliders,
                tooltip: pageTitles.parkingsAdmin,
                hasPermission: permissionParkingAdminSettings,
            },
            {
                link: `parking-management/${parkingId}`,
                icon: faCircleParking,
                tooltip: pageTitles.parkings,
                hasPermission: permissionParking,
            },
            {
                link: 'profile',
                icon: faUser,
                tooltip: pageTitles.profile,
                hasPermission: permissionProfile,
            },
        ];
    }, [parkingId]);

    const navigationLinksWithPermissions: NavigationLinkDetail[] = React.useMemo(() => {
        return navigationLinkDetails.filter((item) => item.hasPermission);
    }, [navigationLinkDetails]);

    const hamburgerMenuNavigationDetails: NavigationLinkDetail[] = React.useMemo(() => {
        return navigationLinksWithPermissions.filter(
            (item) => !visibleNavLinksInMobile.some((hamburgerItem) => item.link === hamburgerItem.link),
        );
    }, [navigationLinksWithPermissions, visibleNavLinksInMobile]);

    const hamburgerMenuItems: NavigationLinkDetail[] = hamburgerMenuNavigationDetails.filter(
        (item) => item.link !== 'profile',
    );
    const listOfVisibleMobileNavLinks: string[] = visibleNavLinksInMobile.map((item) => item.link);

    React.useEffect(() => {
        const isMobile = screenWidth < 640;
        const isLargePhone = screenWidth > 400 && screenWidth < 640;
        const numberOfNavLinksShownOnMobile = isMobile && isLargePhone ? 4 : 3;

        const visibleNavLinks = navigationLinksWithPermissions.slice(
            0,
            isMobile ? numberOfNavLinksShownOnMobile : navigationLinksWithPermissions.length,
        );

        setVisibleNavLinksInMobile(visibleNavLinks.map((item) => item));
    }, [screenWidth, parkingId]);

    return (
        <aside className="bg-primary-700 fixed top-0 left-0 z-40 flex h-16 w-full flex-col overflow-x-hidden overflow-y-auto sm:h-full sm:w-20">
            <div className="mx-4 my-3 flex size-10 shrink-0 items-center justify-center rounded-md sm:m-4 sm:size-12 sm:bg-white sm:p-2 dark:sm:bg-gray-950">
                <img src={logo} alt="SmartPark" />
            </div>
            <nav className="bg-primary-700 text-primary-300 fixed bottom-0 flex w-full justify-around p-2 text-xl sm:relative sm:h-full sm:flex-col sm:justify-normal sm:gap-3 sm:px-4 sm:pt-0 sm:pb-4 dark:bg-gray-950">
                {navigationLinkDetails.slice(0, navigationLinkDetails.length - 2).map((item) => {
                    {
                        return (
                            item.hasPermission && (
                                <>
                                    {item.link === 'pay-stations' && <Separator className="bg-primary-200" />}
                                    <NavigationSidebarButton
                                        className={cn(
                                            !listOfVisibleMobileNavLinks.includes(item.link) && 'hidden sm:flex',
                                        )}
                                        icon={item.icon}
                                        link={item.link}
                                        tooltip={formatMessage(item.tooltip)}
                                        key={item.link}
                                    />
                                </>
                            )
                        );
                    }
                })}

                <div className="mt-auto hidden sm:flex" />

                {navigationLinkDetails.slice(navigationLinkDetails.length - 2).map((item) => {
                    const isCustomActive =
                        item.link !== 'profile' && location.pathname.startsWith('/parking-management');
                    {
                        return (
                            item.hasPermission && (
                                <NavigationSidebarButton
                                    className={cn(
                                        item.link !== 'profile' &&
                                            !listOfVisibleMobileNavLinks.includes(item.link) &&
                                            'hidden sm:flex',
                                    )}
                                    icon={item.icon}
                                    link={item.link}
                                    tooltip={formatMessage(item.tooltip)}
                                    isCustomActive={isCustomActive}
                                    key={item.link}
                                />
                            )
                        );
                    }
                })}

                {hamburgerMenuItems.length > 0 && visibleNavLinksInMobile.length > 0 && (
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger className="hover:bg-primary-600 data-[state=open]:bg-primary-600 xs:size-12 flex size-10 shrink-0 items-center justify-center rounded-md transition-colors data-[state=open]:text-white">
                            <FontAwesomeIcon icon={faBars} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="bg-primary-700 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom z-20 mb-1 w-svw rounded-t-3xl rounded-b-none border-0 p-6 pb-3 text-white"
                        >
                            {hamburgerMenuItems.map((item) => {
                                {
                                    return (
                                        <NavLink to={item.link} key={item.link}>
                                            <DropdownMenuItemWithIcon
                                                variant="sidebar"
                                                iconClassName="text-primary-300"
                                                icon={item.icon}
                                            >
                                                <FormattedMessage {...item.tooltip} />
                                            </DropdownMenuItemWithIcon>
                                        </NavLink>
                                    );
                                }
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </nav>
        </aside>
    );
};

export default NavigationSidebar;
